// imports
import { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// local imports
import { AdditionalLayer } from '../../../../../configs/additionalLayers';

interface LayerLegendProps {
  layer: AdditionalLayer;
}

const LegendContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: calc(100% - 20px);
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  background-color: transparent;
`;

const LegendHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding-bottom: 8px;
  border-bottom: ${({ isExpanded }) =>
    isExpanded ? '1px solid #e0e0e0' : 'none'};
  margin-left: -8px;
  margin-right: -8px;
  padding-left: 8px;
  padding-right: 8px;
`;

const LegendItemsContainer = styled('ul')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  max-height: 250px;
  overflow-y: scroll;
  list-style: none;
  margin-left: 0;
  padding: 0;
`;

const LegendItem = styled('li')`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * LayerLegend
 *
 * Renders a collapsible legend for any layer with a colormap definition.
 *
 * @param layer - Any AdditionalLayer object with a colormap
 * @returns {JSX.Element | null} The rendered legend component or null if no colormap
 */
export default function LayerLegend({ layer }: LayerLegendProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Only render if layer has a colormap
  const hasColormap =
    'colormap' in layer &&
    layer.colormap &&
    Object.keys(layer.colormap).length > 0;

  if (!hasColormap) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <LegendContainer data-testid="layer-legend">
      <LegendHeader onClick={toggleExpanded} isExpanded={isExpanded}>
        <Typography
          component="h4"
          variant="body2"
          sx={{ fontWeight: 'bold', margin: 0 }}
        >
          {layer.name} Legend
        </Typography>
        <IconButton
          size="small"
          aria-label={isExpanded ? 'Collapse legend' : 'Expand legend'}
          sx={{ padding: 0 }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </LegendHeader>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <LegendItemsContainer aria-label="Color classification">
          {Object.entries(layer.colormap!).map(([label, color]) => (
            <LegendItem key={label}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  borderRadius: '4px',
                  backgroundColor: `${color}`,
                  border: '1px solid #ccc',
                }}
              />
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                }}
              >
                {label}
              </Typography>
            </LegendItem>
          ))}
        </LegendItemsContainer>
      </Collapse>
    </LegendContainer>
  );
}
